/**
 * A contingência da SENATRAN (`R-06`, ADR-077), lida pela moldura e dada às telas.
 *
 * Relida a cada 30 segundos e a cada troca de tela, só com a aba visível — a tela escondida não
 * pergunta —, e na hora em que uma ação avisa que ela pode ter mudado
 * (`avisarQueAContingenciaPodeTerMudado`). Só com o TollVision licenciado e para quem não é o
 * regulador (`OB-13`): a rota não é aberta a ele, e a recusa iria à trilha dele. Falha na
 * leitura guarda a última que deu certo: a faixa não some porque uma releitura caiu.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { EVENTO_DA_CONTINGENCIA } from "../api/contingencia";
import { api } from "../api/cliente";
import type { Contingencia } from "../api/tipos";

export const RELEITURA_DA_CONTINGENCIA_MS = 30_000;

export type ContingenciaNaMoldura = {
  contingencia: Contingencia | null;
  /** A resposta do *Tentar agora* já traz a contingência de depois: sem reler. */
  definir: (c: Contingencia) => void;
};

export function useContingencia(
  ligado: boolean,
  gatilho: string,
): ContingenciaNaMoldura {
  const [contingencia, definirContingencia] = useState<Contingencia | null>(
    null,
  );
  useEffect(() => {
    if (!ligado) return;
    let vivo = true;
    const ler = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        const lida = await api<Contingencia>(
          "/tollvision/senatran/contingencia",
        );
        if (vivo) definirContingencia(lida);
      } catch {
        // Fica a última leitura: uma releitura que caiu não diz que a SENATRAN voltou.
      }
    };
    void ler();
    const id = window.setInterval(
      () => void ler(),
      RELEITURA_DA_CONTINGENCIA_MS,
    );
    const aoAvisar = () => void ler();
    window.addEventListener(EVENTO_DA_CONTINGENCIA, aoAvisar);
    return () => {
      vivo = false;
      window.clearInterval(id);
      window.removeEventListener(EVENTO_DA_CONTINGENCIA, aoAvisar);
    };
  }, [ligado, gatilho]);
  const definir = useCallback((c: Contingencia) => definirContingencia(c), []);
  return { contingencia: ligado ? contingencia : null, definir };
}

export const ContextoDaContingencia = createContext<Contingencia | null>(null);

/** A contingência que a moldura leu, para as telas (a SENATRAN mostra a aba da fila). */
export function useContingenciaDaMoldura(): Contingencia | null {
  return useContext(ContextoDaContingencia);
}
